/**
 * Card Panel Grid Pagination
 * --------------------------------------------------
 * Handles show more/less functionality for card grids
 * JavaScript manages all visibility by adding/removing 'hidden' class
 * Responsive: Adjusts card count based on grid columns at different breakpoints
 *
 * Usage:
 * - Add data-show-card-count to set the increment/baseline amount
 * - Add button with data-show-more-less-toggle to trigger pagination
 * - Add data-show-more-text and data-show-less-text to button for dynamic text
 */

const _debounce = require('lodash.debounce');

window.addEventListener('load', () => {
    const cardPanelGrids = document.querySelectorAll('.card-panel-grid, .card-panel-slider-to-grid');

    cardPanelGrids.forEach(panel => {
        const buttonContainer = panel.querySelector('[data-show-more-less-container]');
        const toggleButton = panel.querySelector('button[data-show-more-less-toggle]');

        // Only initialize if the panel has a toggle button
        if (!toggleButton || !buttonContainer) return;

        const cardContainer = panel.querySelector('[data-card-container]');
        if (!cardContainer) return;

        // Get configuration
        const limitCards = parseInt(cardContainer.dataset.limitCards) || 6;

        // Get cards - handle both grid and slider-to-grid panels
        const swiperWrapper = cardContainer.querySelector('.swiper-wrapper');
        const cards = swiperWrapper
            ? Array.from(swiperWrapper.children)
            : Array.from(cardContainer.children);
        const totalCards = cards.length;

        // Get button text from data attributes
        const showMoreText = toggleButton.dataset.showMoreText || 'Show more';
        const showLessText = toggleButton.dataset.showLessText || 'Show less';

        const buttonTextSpan = toggleButton.querySelector('span');

        // Track state
        let isShowingAll = false;
        let showMoreClickCount = 0; // Track how many times "show more" was clicked
        let userHasInteracted = false; // Track if user has clicked show more/less

        /**
         * Detect grid columns based on container classes and current viewport
         */
        function getGridColumns() {
            const containerClasses = cardContainer.className;
            const width = window.innerWidth;

            // Check for lg breakpoint (1024px+)
            if (width >= 1024) {
                if (containerClasses.includes('lg:grid-cols-4')) return 4;
                if (containerClasses.includes('lg:grid-cols-3')) return 3;
                if (containerClasses.includes('lg:grid-cols-2')) return 2;
            }

            // Check for md breakpoint (768px+)
            if (width >= 768) {
                if (containerClasses.includes('md:grid-cols-4')) return 4;
                if (containerClasses.includes('md:grid-cols-3')) return 3;
                if (containerClasses.includes('md:grid-cols-2')) return 2;
            }

            // Mobile - single column
            return 1;
        }

        /**
         * Get the increment amount for current breakpoint
         * Fixed increments: 3 (mobile), 4 (tablet), 6 (desktop)
         */
        function getIncrementAmount() {
            const width = window.innerWidth;

            // Mobile (< 768px) - increment by 3
            if (width < 768) {
                return 3;
            }
            // Tablet (768-1023px) - increment by 4
            if (width >= 768 && width < 1024) {
                return 4;
            }
            // Desktop (1024px+) - increment by 6
            return 6;
        }

        /**
         * Get the baseline card count for current breakpoint
         * Uses fixed increments per breakpoint
         * If user hasn't interacted, uses mobile/tablet limits or limitCards for desktop
         */
        function getBaselineCount() {
            const width = window.innerWidth;

            // If user hasn't interacted, apply mobile/tablet limits
            if (!userHasInteracted) {
                // Mobile (< 768px) - limit to 3
                if (width < 768) {
                    return Math.min(3, totalCards);
                }
                // Tablet (768-1023px) - limit to 4
                if (width >= 768 && width < 1024) {
                    return Math.min(4, totalCards);
                }
                // Desktop - use limitCards from CMS
                return Math.min(limitCards, totalCards);
            }

            // User has interacted - use increment amount
            return getIncrementAmount();
        }

        /**
         * Calculate how many cards should be visible based on click count
         */
        function getTargetVisibleCount() {
            if (isShowingAll) {
                return totalCards;
            }

            const increment = getIncrementAmount();
            const targetCount = increment * (showMoreClickCount + 1);

            return Math.min(targetCount, totalCards);
        }

        /**
         * Get count of currently visible cards
         */
        function getVisibleCount() {
            return cards.filter(card => !card.classList.contains('hidden')).length;
        }

        /**
         * Get count of currently hidden cards
         */
        function getHiddenCount() {
            return cards.filter(card => card.classList.contains('hidden')).length;
        }

        /**
         * Check if panel is in swiper mode (mobile/tablet for slider-to-grid)
         */
        function isInSwiperMode() {
            const isSliderToGrid = panel.classList.contains('card-panel-slider-to-grid');
            const width = window.innerWidth;
            // Slider-to-grid uses swiper below 1024px
            return isSliderToGrid && width < 1024;
        }

        /**
         * Update card visibility based on how many should be shown
         * Skip if in swiper mode (let swiper handle visibility)
         */
        function updateCardVisibility(showCount) {
            // Don't hide cards when in swiper mode
            if (isInSwiperMode()) {
                // Remove any hidden classes that might exist
                cards.forEach(card => card.classList.remove('hidden'));
                return;
            }

            cards.forEach((card, index) => {
                if (index < showCount) {
                    card.classList.remove('hidden');
                } else {
                    card.classList.add('hidden');
                }
            });
        }

        /**
         * Update button text and visibility based on current state
         */
        function updateButtonText() {
            // Hide button when in swiper mode
            if (isInSwiperMode()) {
                buttonContainer.classList.remove('flex');
                buttonContainer.classList.add('hidden');
                return;
            }

            const hiddenCount = getHiddenCount();
            const visibleCount = getVisibleCount();
            const baseline = getBaselineCount();

            // Determine if button should be visible
            // Show button if: there are hidden cards OR we're showing more than baseline
            const shouldShowButton = hiddenCount > 0 || visibleCount > baseline;

            // Toggle button container visibility
            if (shouldShowButton) {
                buttonContainer.classList.remove('hidden');
                buttonContainer.classList.add('flex');
            } else {
                buttonContainer.classList.remove('flex');
                buttonContainer.classList.add('hidden');
            }

            // Update button text
            if (hiddenCount === 0) {
                // All cards are showing, button should say "Show less"
                if (buttonTextSpan) {
                    buttonTextSpan.textContent = showLessText;
                }
            } else {
                // More cards to show, button should say "Show more"
                if (buttonTextSpan) {
                    buttonTextSpan.textContent = showMoreText;
                }
            }
        }

        /**
         * Handle button click - toggle between show more and show less
         */
        function handleToggle() {
            const hiddenCount = getHiddenCount();

            if (hiddenCount === 0 || isShowingAll) {
                // Currently showing all cards, reset to baseline
                isShowingAll = false;
                showMoreClickCount = 0;
                userHasInteracted = false; // Reset interaction flag

                const baseline = getBaselineCount();
                updateCardVisibility(baseline);
                updateButtonText();

                // Scroll to panel on mobile when showing less
                if (window.innerWidth < 768) {
                    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } else {
                // Show more cards
                userHasInteracted = true; // Mark that user has interacted
                showMoreClickCount++;
                const targetCount = getTargetVisibleCount();
                updateCardVisibility(targetCount);

                // Track if we're now showing all
                if (targetCount >= totalCards) {
                    isShowingAll = true;
                }

                updateButtonText();
            }
        }

        /**
         * Handle window resize - adjust visible cards based on breakpoint
         */
        function handleResize() {
            const currentVisible = getVisibleCount();
            const gridCols = getGridColumns();
            const increment = getIncrementAmount();
            const baseline = getBaselineCount();

            // If showing all, don't adjust
            if (isShowingAll) {
                return;
            }

            // If user hasn't interacted, reset to baseline for current breakpoint
            if (!userHasInteracted) {
                updateCardVisibility(baseline);
                updateButtonText();
                return;
            }

            // User has interacted - adjust to nearest multiple of grid columns
            let newCount = currentVisible;

            // For 2-column grids, round up to nearest even number
            if (gridCols === 2 && currentVisible % 2 !== 0) {
                newCount = currentVisible + 1;
            }
            // For 3-column grids, round up to nearest multiple of 3
            else if (gridCols === 3 && currentVisible % 3 !== 0) {
                newCount = Math.ceil(currentVisible / 3) * 3;
            }
            // For 4-column grids, round up to nearest multiple of 4
            else if (gridCols === 4 && currentVisible % 4 !== 0) {
                newCount = Math.ceil(currentVisible / 4) * 4;
            }

            // Don't exceed total cards
            newCount = Math.min(newCount, totalCards);

            // Update visibility if count changed
            if (newCount !== currentVisible) {
                updateCardVisibility(newCount);

                // Check if we're now showing all
                if (newCount >= totalCards) {
                    isShowingAll = true;
                }
            }

            updateButtonText();
        }

        // Debounced resize handler
        const debouncedResize = _debounce(handleResize, 16.6667);

        // Initialize
        toggleButton.addEventListener('click', handleToggle);
        window.addEventListener('resize', debouncedResize);

        // Set initial visibility
        const initialCount = getBaselineCount();
        updateCardVisibility(initialCount);
        updateButtonText();
    });
});

